import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { listAIInputAssets } from "../../../core/utils/aiInputAssetsApi";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";
import { CloseIcon } from "./icons/AIIcons";

/**
 * AIAssetLibraryModal
 * - Lists ALL assets in the user's library (across websites)
 * - Only allows selecting assets with status === 'completed'
 */
const AIAssetLibraryModal = ({ isOpen, onClose, onSelect, disabledAssetIds = [], maxSelectable = 5 }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assets, setAssets] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await listAIInputAssets();
        const results = data?.results || [];
        if (mounted) setAssets(results);
      } catch (e) {
        if (mounted) setError(e?.message || "Failed to load assets");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    const list = Array.isArray(assets) ? assets : [];
    const sorted = [...list].sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0));
    if (!q) return sorted;
    return sorted.filter((a) => String(a?.original_filename || "").toLowerCase().includes(q));
  }, [assets, query]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[99999]" onClick={onClose}>
      <div className="absolute inset-0 bg-black dark:bg-dark-950 bg-opacity-50 dark:bg-opacity-70" />
      <div className="absolute inset-0 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <div className={`${surfaces.elevated} w-full max-w-lg rounded-lg shadow-xl border ${borders.default} overflow-hidden`}>
          <div className={`flex items-center justify-between px-4 py-3 border-b ${borders.default} ${surfaces.secondary}`}>
            <div>
              <div className={`${typography.bodySmall} ${textColors.primary}`}>Choose from uploaded</div>
              <div className={`${typography.caption} ${textColors.muted}`}>Select a completed asset to pin to this chat (max {maxSelectable}).</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`p-1.5 rounded-md ${surfaces.hover} ${textColors.secondary}`}
              aria-label="Close"
              title="Close"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by filename…"
              className={`w-full px-3 py-2 rounded-lg border ${borders.default} ${surfaces.primary} ${textColors.primary} ${typography.bodySmall} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400`}
            />
          </div>

          <div className={`max-h-[360px] overflow-y-auto border-t border-b ${borders.default}`}>
            {loading ? (
              <div className={`p-6 text-center ${typography.bodySmall} ${textColors.secondary}`}>Loading assets…</div>
            ) : error ? (
              <div className={`p-6 text-center ${typography.bodySmall} text-red-600 dark:text-red-400`}>{error}</div>
            ) : filtered.length === 0 ? (
              <div className={`p-6 text-center ${typography.bodySmall} ${textColors.secondary}`}>No assets found.</div>
            ) : (
              <div className={`divide-y ${borders.default}`}>
                {filtered.map((a) => {
                  const id = a?.id;
                  const filename = a?.original_filename || `Asset #${id}`;
                  const isCompleted = a?.status === "completed";
                  const isDisabled = !isCompleted || disabledAssetIds.includes(id);
                  const subtitle = isCompleted ? (a?.asset_type === "image" ? "Image" : "File") : `Not ready (${a?.status || "unknown"})`;
                  return (
                    <button
                      key={id}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => onSelect?.(a)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        isDisabled ? "opacity-50 cursor-not-allowed" : `${surfaces.hover}`
                      }`}
                      title={isDisabled ? "Only completed assets can be pinned" : "Pin to chat"}
                    >
                      <div className={`${typography.bodySmall} ${textColors.primary} truncate`}>{filename}</div>
                      <div className={`${typography.caption} ${textColors.muted} mt-0.5`}>{subtitle}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className={`px-4 py-3 border-t ${borders.default}`}>
            <div className={`${typography.caption} ${textColors.muted}`}>
              Tip: After pinning, ask AI to “use asset #123” or “add my CV PDF”.
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AIAssetLibraryModal;

